/*
 * ******************************************************************
 * Copyright (c) 2017-2025 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */
import { Component, HostBinding, Input } from "@angular/core";

/**
 *
 *      Layout grid component based on display:flex;
 *
 *      <layout></layout>
 *      Built in directives:
 *          1. col="0 | 1(default) | 2 | 3"
 *                 - Sets the number of columns.
 *          2. align="stretch(default) | inherit | start | end | center"
 *                  - Aligns the elements in single direction (column | row)
 *          3. justify="start(default) | end | center"
 *                 - Justifies the elements content
 *          4. direction="column(default) | row"
 *                 - Sets the elements direction
 *       Attributes:
 *          1.extend
 *             - Extends the height of the child wrapper to match the parent of layout
 *             - general case:
 *                <layout extend>
 *                   insert wrapper here
 *                </layout>
 *          2. scroll-y
 *             - Vertically scrolls the content inside <layout>
 *          3. f0
 *             - stands for flex: 0 0 auto;
 *             - When applied to a DOM element it makes the element grow by its own box model without a ratio
 *          4. fi
 *             - stands for flex: initial;
 *          5. f-g1
 *             - When applied to a DOM element it makes the element grow with ratio 1 compared to its siblings
 *             - If our element has no siblings it will take the growth of its parent
 *             - E.g.: If our element has 1 sibling and both have f-g1 they will be compared with ratio 1:1 or relative 50% of its parent
 *          6. f-g2
 *             - When applied to a DOM element it makes the element grow with ratio 2 compared to its siblings
 *             - If our element has no siblings it will take the growth of its parent
 *             - E.g.: If our element has 1 sibling and both have f-g2 they will be compared 2:2 or take 50% of the parent.
 *               But if one is f-g1 and other f-g2 they will be compared with ratio 1:2 or relative 33.33% and 66.66% of its parent
 *          7. f-g3
 *             - When applied to a DOM element it makes the element grow with ratio 3 compared to its siblings
 *             - If our element has no siblings it will take the growth of its parent
 *             - E.g.: If one element is f-g1 and other f-g3 they will be compared with ratio 1:3 or relative 25% and 75% of its parent
 *          8. f-g4
 *             - When applied to a DOM element it makes the element grow with ratio 4 compared to its siblings
 *             - If our element has no siblings it will take the growth of its parent
 *             - E.g.: If one element is f-g1 and other f-g4 they will be compared with ratio 1:4 or relative 20% and 80% of its parent
 *          9. f-g5
 *             - When applied to a DOM element it makes the element grow with ratio 5 compared to its siblings
 *             - If our element has no siblings it will take the growth of its parent
 *             - E.g.: If one element is f-g1 and other f-g5 they will be compared with ratio 1:5 or relative 16.66% and 83.33% of its parent
 *
 *      **Note: Use wrappers inside <layout></layout>
 *
 */


// @formatter:off
@Component({
    standalone: false,
   selector: 'layout',
   templateUrl: 'layout.component.html',
   styleUrls: ['layout.component.scss']
})
// @formatter:on

export class LayoutComponent {

    @HostBinding("attr.class") hostClass = "";

    constructor() {
    }

    @Input()
    set col(value: number) {
        this.hostClass += "layout-col-" + value + " ";
    }

    @Input()
    set direction(value: number) {
        this.hostClass += "direction-" + value + " ";
    }

    @Input()
    set align(value: string) {
        this.hostClass += "align-" + value + " ";
    }

    @Input()
    set justify(value: string) {
        this.hostClass += "justify-" + value + " ";
    }
}
